import clsx from 'clsx';
import React, { ReactElement, useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { PartialDeep } from 'type-fest';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import Modal from '@/components/modal/modal';
import OptionRow from '@/components/optionRow/optionRow';
import { Heading } from '@/components/typography/heading';
import { useModelsQuery } from '@/hooks/queries/useModelsQuery';
import {
  METRIC_TYPES,
  MetricConfigAPI,
  MetricConfigAPIHeader,
  MetricConfigLLMScore,
  MetricCreateReq,
  MetricTypeEnum
} from '@/types/evals';

import styles from './modalMetricCreate.module.scss';

type Props = {
  cb: ( ret: MetricCreateReq ) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  mode?: 'create' | 'clone' | 'version';
  initial?: (
    | {
      type: typeof METRIC_TYPES.API_CLASSIFIER;
      config: Partial<MetricConfigAPI> | PartialDeep<MetricConfigLLMScore>;
    }
    | {
      type: typeof METRIC_TYPES.LLM_AS_A_JUDGE;
      config: PartialDeep<MetricConfigLLMScore>;
    }
  ) & {
    producesMetrics: string[];
    exampleType: ( 'message' | 'conversation' )[];
    name?: string;
    description?: string;
  };
};

export default function ModalMetricCreate( props: Props ) {
  /* DATA */
  const { cb, onClose, isSubmitting, mode = 'create' } = props;

  enum StepsEnum {
    CREATE,
    METRIC_TYPE
  }

  const INPUT_CONSTS = {
    API_URL: 'API Url',
    DESCRIPTION: 'Description',
    EXAMPLE_TYPES: 'Example Types',
    LLM_MODEL: 'LLM',
    LLM_PROMPT: 'Grading Prompt',
    LLM_TEMPERATURE: 'Temperature',
    LLM_PROMPT_PARAMETERS: 'Prompt Parameters',
    METRIC_TYPE: 'Select Metric Type',
    NAME: 'Name',
    PRODUCES_METRICS: 'Produces Metrics',
    IS_ASYNC: 'Is metric async'
  } as const;
  type InputEnums = ( typeof INPUT_CONSTS )[keyof typeof INPUT_CONSTS];

  const inputsByStep: {
    [step in StepsEnum]: {
      [input in InputEnums]?: {
        filter?: () => boolean;
        validate: () => boolean;
      };
    };
  } = {
    [StepsEnum.CREATE]: {
      [INPUT_CONSTS.DESCRIPTION]: {
        validate: () => !description
      },
      [INPUT_CONSTS.NAME]: {
        validate: () => !name
      },
      [INPUT_CONSTS.METRIC_TYPE]: {
        validate: () => !metricType
      }
    },
    [StepsEnum.METRIC_TYPE]: {
      [INPUT_CONSTS.API_URL]: {
        filter: () => metricType === METRIC_TYPES.API_CLASSIFIER,
        validate: () => !mTypeApiUrl
      },
      [INPUT_CONSTS.EXAMPLE_TYPES]: {
        validate: () => !Object.values( exampleTypes ).filter( ( el ) => !!el ).length
      },
      [INPUT_CONSTS.LLM_MODEL]: {
        filter: () => metricType === METRIC_TYPES.LLM_AS_A_JUDGE,
        validate: () => !mTypeLLMModel
      },
      [INPUT_CONSTS.LLM_PROMPT]: {
        filter: () => metricType === METRIC_TYPES.LLM_AS_A_JUDGE,
        validate: () => !mTypeLLMPrompt
      },
      [INPUT_CONSTS.LLM_PROMPT_PARAMETERS]: {
        filter: () => metricType === METRIC_TYPES.LLM_AS_A_JUDGE,
        validate: () => !arePromptParametersComplete()
      },
      [INPUT_CONSTS.PRODUCES_METRICS]: {
        validate: () => !producesMetrics.length
      },
      [INPUT_CONSTS.IS_ASYNC]: {
        filter: () => metricType === METRIC_TYPES.API_CLASSIFIER,
        validate: () => typeof isAsync !== 'boolean'
      }
    }
  };

  /* INITIAL STATE */
  const initialExampleTypes = props.initial?.exampleType ?? [];
  const initialProducesMetrics = props.initial?.producesMetrics ?? [];
  const initialApiConfig = ( props.initial?.config ??
    {} ) as Partial<MetricConfigAPI>;
  const initialLLMConfig = ( props.initial?.config ??
    {} ) as PartialDeep<MetricConfigLLMScore>;

  /* STATE */
  const [ name, setName ] = useState<string>(
    mode === 'clone' && props.initial?.name ? `${ props.initial.name } (CLONE)` : props.initial?.name ?? ''
  );
  const [ description, setDescription ] = useState<string>(
    mode === 'clone' && props.initial?.description ? `${ props.initial.description } (CLONE)` : props.initial?.description ?? ''
  );
  const [ exampleTypes, setExampleTypes ] = useState<{
    conversation: boolean;
    message: boolean;
  }>( {
    conversation: initialExampleTypes.includes( 'conversation' ),
    message: initialExampleTypes.includes( 'message' )
  } ); // the default state determines what options show in the UI

  const [ metricType, setMetricType ] = useState<MetricTypeEnum | undefined>(
    props.initial?.type
  );

  const [ modelNames, setModelNames ] = useState<string[]>( [] );
  const { data: models, error: modelsFetchError } = useModelsQuery( { enabled: metricType === METRIC_TYPES.LLM_AS_A_JUDGE } );
  useEffect( () => {
    if ( models ) setModelNames( models.map( ( el ) => el.name ) );
  }, [ models ] );

  useEffect( () => {
    if ( modelsFetchError ) {
      console.error( 'unable to get models' );
      toast( 'unable to get models' );
    }
  }, [ modelsFetchError ] );

  const [ mTypeApiHeaders, setMTypeApiHeaders ] = useState<
    MetricConfigAPIHeader[]
  >( initialApiConfig.headers || [ { header: 'authorization', value: '' } ] );
  const [ mTypeApiUrl, setMTypeApiUrl ] = useState<string | undefined>(
    initialApiConfig.url
  );

  const [ mTypeLLMModel, setMTypeLLMModel ] = useState<string | undefined>(
    initialLLMConfig.modelParameters?.model
  );
  const [ mTypeLLMPrompt, setMTypeLLMPrompt ] = useState<string | undefined>(
    initialLLMConfig.prompt
  );
  const [ mTypeLLMTemp, setMTypeLLMTemp ] = useState<number | undefined>(
    parseFloat( initialLLMConfig.modelParameters?.temperature ?? 'NaN' ) ||
      undefined
  );
  const [ mTypeLLMPromptParams, setMTypeLLMPromptParams ] = useState<Record<string, string>>(
    ( initialLLMConfig.promptParameters as Record<string, string> ) || {}
  );

  const [ producesMetrics, setProducesMetrics ] = useState<string[]>(
    initialProducesMetrics
  );

  const [ step, setStep ] = useState<StepsEnum>(
    props.initial && mode !== 'clone' ? StepsEnum.METRIC_TYPE : StepsEnum.CREATE
  );
  const [ validationErrKeys, setValidationErrKeys ] = useState<InputEnums[]>( [] );

  const [ isAsync, setIsAsync ] = useState<boolean>( false );

  /* HELPERS */

  const _inputRequiredCheck = ( input: InputEnums ): undefined | string => {
    const isMissing = validationErrKeys.includes( input );

    if ( isMissing ) return styles.required;
    return;
  };

  const isPromptParamValueMissing = ( key: string ): boolean => {
    // Only highlight if validation has been triggered and the parameter value is empty
    if ( !validationErrKeys.includes( INPUT_CONSTS.LLM_PROMPT_PARAMETERS ) ) return false;

    const value = mTypeLLMPromptParams[key];
    return !value || value.trim() === '';
  };

  const arePromptParametersComplete = (): boolean => Object.values( mTypeLLMPromptParams ).every( ( value ) => value?.trim().length > 0 );

  const _inputValidator = async() => {
    const missingRequiredInputs: InputEnums[] = [];

    for ( const [ key, input ] of Object.entries( inputsByStep[step] ) ) {
      if ( input.filter ) {
        const relevant = input.filter();
        if ( !relevant ) continue;
      }

      if ( input.validate() ) missingRequiredInputs.push( key as InputEnums );
    }

    if ( missingRequiredInputs.length ) {
      setValidationErrKeys( missingRequiredInputs );
      throw Error( 'missing inputs: ' + missingRequiredInputs.join( ', ' ) );
    }
  };

  const _step = async() => {
    setValidationErrKeys( [] ); // reset

    try {
      await _inputValidator();
    } catch( e ) {
      console.info( ( e as Error ).message );
      return;
    }

    if ( step === StepsEnum.METRIC_TYPE ) {
      onComplete();
    } else {
      setStep( ( prevStep ) => prevStep + 1 );
    }
  };

  const _stepBack = () => {
    if ( step === StepsEnum.CREATE ) return;
    setStep( ( prevStep ) => prevStep - 1 );
  };

  const _stepper = () => {
    let ret: ReactElement | undefined;

    switch ( step ) {
      case StepsEnum.CREATE: {
        ret = renderStepCreate();
        break;
      }
      case StepsEnum.METRIC_TYPE: {
        ret = renderStepType();
        break;
      }
      default: {
        throw new Error( 'unknown step' );
      }
    }

    return ret;
  };

  /* HANDLERS */
  const onApiHeaderChange = (
    index: number,
    side: 'header' | 'value',
    val: string
  ) => {
    let next;

    const exists = index < mTypeApiHeaders.length;

    if ( exists ) {
      next = [
        ...mTypeApiHeaders.slice( 0, index ),
        { ...mTypeApiHeaders[index], [side]: val },
        ...mTypeApiHeaders.slice( index + 1 )
      ];
    } else {
      next = [ ...mTypeApiHeaders, { header: '', value: '', [side]: val } ];
    }

    setMTypeApiHeaders( next );
  };

  const onPromptParamAdd = () => {
    const newKey = `param_${ Object.keys( mTypeLLMPromptParams ).length + 1 }`;
    setMTypeLLMPromptParams( { ...mTypeLLMPromptParams, [newKey]: '' } );
  };

  const onPromptParamRemove = ( key: string ) => {
    const next = { ...mTypeLLMPromptParams };
    delete next[key];
    setMTypeLLMPromptParams( next );
  };

  const onPromptParamChange = ( oldKey: string, newKey: string, value: string ) => {
    const next = { ...mTypeLLMPromptParams };
    if ( oldKey !== newKey && oldKey in next ) {
      delete next[oldKey];
    }
    if ( newKey ) {
      next[newKey] = value;
    }
    setMTypeLLMPromptParams( next );
  };

  const onComplete = () => {
    let configuration: MetricConfigAPI | MetricConfigLLMScore | undefined;

    if ( metricType === METRIC_TYPES.API_CLASSIFIER ) {
      configuration = {
        url: mTypeApiUrl!,
        headers: mTypeApiHeaders,
        isAsync: isAsync
      };
    } else if ( metricType === METRIC_TYPES.LLM_AS_A_JUDGE ) {
      configuration = {
        prompt: mTypeLLMPrompt!,
        modelParameters: {
          model: mTypeLLMModel!,
          temperature: mTypeLLMTemp?.toString() || '0'
        },
        ...( Object.keys( mTypeLLMPromptParams ).length > 0 && { promptParameters: mTypeLLMPromptParams } )
      };
    }

    if ( !configuration ) {
      toast( 'unable to build configuration' );
      return;
    }

    const ret: MetricCreateReq = {
      configuration,
      description,
      exampleType: Object.entries( exampleTypes )
        .filter( ( [ _, val ] ) => val )
        .map( ( [ key ] ) => key as 'message' | 'conversation' ),
      name,
      producesMetrics
    };

    cb( ret );
  };

  const onSelectExampleType = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    const { checked, value } = e.currentTarget;

    const next = {
      ...exampleTypes,
      [value]: checked
    };

    setExampleTypes( next );
  };

  /* RENDERS */
  const renderStepCreate = (): ReactElement => (
    <>
      <Heading as="h2" size="md" data-testid="modal-title">{mode === 'clone' ? 'Clone' : 'Create'} Metric </Heading>
      <div className={ clsx( 'layout__col', 'pad' ) }>
        <label
          className={ clsx(
            'auth_flow_label',
            _inputRequiredCheck( INPUT_CONSTS.NAME )
          ) }
          htmlFor={ INPUT_CONSTS.NAME }
          data-required-error={ _inputRequiredCheck( INPUT_CONSTS.NAME ) ? '' : undefined }
        >
          {INPUT_CONSTS.NAME}
        </label>
        <input
          className={ clsx( 'auth_flow_input' ) }
          defaultValue={ name }
          id={ INPUT_CONSTS.NAME }
          name={ INPUT_CONSTS.NAME }
          onChange={ ( e ) => setName( e.target.value ) }
          type="text"
          data-testid="metric-name-input"
        />
      </div>
      <div className={ clsx( 'layout__col', 'pad' ) }>
        <label
          className={ clsx(
            'auth_flow_label',
            _inputRequiredCheck( INPUT_CONSTS.DESCRIPTION )
          ) }
          htmlFor={ INPUT_CONSTS.DESCRIPTION }
          data-required-error={ _inputRequiredCheck( INPUT_CONSTS.DESCRIPTION ) ? '' : undefined }
        >
          {INPUT_CONSTS.DESCRIPTION}
        </label>
        <input
          className={ clsx( 'auth_flow_input' ) }
          defaultValue={ description }
          id={ INPUT_CONSTS.DESCRIPTION }
          name={ INPUT_CONSTS.DESCRIPTION }
          onChange={ ( e ) => setDescription( e.target.value ) }
          type="text"
          data-testid="metric-description-input"
        />
      </div>
      <OptionRow
        value={ metricType }
        onSelect={ ( value: MetricTypeEnum ) => setMetricType( value ) }
        disabled={ mode === 'clone' || mode === 'version' }
        title={
          (
            <p className={ clsx( _inputRequiredCheck( INPUT_CONSTS.METRIC_TYPE ) ) } data-testid="metric-type-label" data-required-error={ _inputRequiredCheck( INPUT_CONSTS.METRIC_TYPE ) ? '' : undefined }>
              {INPUT_CONSTS.METRIC_TYPE}
            </p>
          )
        }
        options={ [
          {
            display: METRIC_TYPES.API_CLASSIFIER,
            value: METRIC_TYPES.API_CLASSIFIER
          },
          {
            display: METRIC_TYPES.LLM_AS_A_JUDGE,
            value: METRIC_TYPES.LLM_AS_A_JUDGE
          }
        ] }
      />
      <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
        <Button
          variant="secondary"
          size="sm"
          onClick={ onClose }
          data-testid="cancel-button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={ _step }
          type="submit"
          data-testid="next-button"
        >
          Next
        </Button>
      </div>
    </>
  );

  const renderStepTypeAPI = (): ReactElement => (
    <>
      <div className={ clsx( 'layout__col', 'pad' ) }>
        <label
          className={ clsx(
            'auth_flow_label',
            _inputRequiredCheck( INPUT_CONSTS.API_URL )
          ) }
          htmlFor="apiUrl"
          data-required-error={ _inputRequiredCheck( INPUT_CONSTS.API_URL ) ? '' : undefined }
        >
          {INPUT_CONSTS.API_URL}
        </label>
        <input
          className="auth_flow_input"
          defaultValue={ mTypeApiUrl }
          id={ INPUT_CONSTS.API_URL }
          name={ INPUT_CONSTS.API_URL }
          onChange={ ( e ) => setMTypeApiUrl( e.target.value ) }
          type="text"
          data-testid="api-url-input"
        />
      </div>

      <div className={ clsx( 'layout__col', 'pad' ) }>
        <fieldset data-testid="headers-section">
          <legend className="auth_flow_label">Headers</legend>
          <div className={ clsx( 'layout__row' ) }>
            <div className={ clsx( 'layout__col' ) }>
              <p className="auth_flow_label">name</p>
              {mTypeApiHeaders.map( ( el: MetricConfigAPIHeader, i: number ) => (
                <input
                  className="auth_flow_input"
                  defaultValue={ el.header }
                  id="apiHeaderKey"
                  key={ `header-${ el.header }-value-${ i }` }
                  name="apiHeaderKey"
                  onChange={ ( e ) => onApiHeaderChange( i, 'header', e.target.value ) }
                  type="text"
                  data-testid={ `header-key-${ i }` }
                />
              ) )}
            </div>
            <div className={ clsx( 'layout__col' ) }>
              <p className="auth_flow_label">value</p>
              {mTypeApiHeaders.map( ( el: MetricConfigAPIHeader, i: number ) => (
                <input
                  className="auth_flow_input"
                  defaultValue={ el.value }
                  id="apiHeaderVal"
                  key={ `header-${ el.header }-name-${ i }` }
                  name="apiHeaderVal"
                  onChange={ ( e ) => onApiHeaderChange( i, 'value', e.target.value ) }
                  type="text"
                  data-testid={ `header-value-${ i }` }
                />
              ) )}
            </div>
          </div>
        </fieldset>
      </div>

      <div className={ clsx( 'layout__col', 'pad' ) }>
        <label className={ clsx( 'auth_flow_label' ) } htmlFor="isAsync" data-testid="async-checkbox-label">
          {INPUT_CONSTS.IS_ASYNC}
        </label>
        <div className={ clsx( 'layout__row', 'flex_left' ) } key={ 'is_async' }>
          <input
            id={ `is_async_${ isAsync }` }
            name="is async"
            onChange={ ( e ) => setIsAsync( e.currentTarget.checked ) }
            type="checkbox"
            value={ 'is_async' }
            checked={ isAsync }
            data-testid="async-checkbox"
          />
          <label htmlFor={ `is_async_${ isAsync }` }>{`${ isAsync }`}</label>
        </div>
      </div>
    </>
  );

  const renderStepTypeLLM = (): ReactElement => (
    <>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <div className={ clsx( 'layout__col' ) }>
          <label
            className={ _inputRequiredCheck( INPUT_CONSTS.LLM_MODEL ) }
            htmlFor={ INPUT_CONSTS.LLM_MODEL }
            data-required-error={ _inputRequiredCheck( INPUT_CONSTS.LLM_MODEL ) ? '' : undefined }
          >
            {INPUT_CONSTS.LLM_MODEL}
          </label>
          <select
            className={ clsx( 'auth_flow_input' ) }
            id={ INPUT_CONSTS.LLM_MODEL }
            onChange={ ( e ) => setMTypeLLMModel( e.currentTarget.value ) }
            value={ mTypeLLMModel }
            data-testid="llm-select"
          >
            <option value="">-</option>
            {modelNames.map( ( model: string, i: number ) => (
              <option key={ `model-${ i }-${ model }` }>{model}</option>
            ) )}
          </select>
        </div>
        <div className={ clsx( 'layout__col', styles.mTypeLLMTemperature ) }>
          <label htmlFor={ INPUT_CONSTS.LLM_TEMPERATURE }>
            {INPUT_CONSTS.LLM_TEMPERATURE}:{' '}
            <span>{mTypeLLMTemp || '0'}</span>
          </label>
          <input
            className={ clsx( 'auth_flow_input' ) }
            id={ INPUT_CONSTS.LLM_TEMPERATURE }
            max="1"
            min="0"
            onChange={ ( e ) => setMTypeLLMTemp( Number( e.target.value ) ) }
            step="0.1"
            type="range"
            value={ mTypeLLMTemp ?? 0 }
            data-testid="temperature-slider"
          />
        </div>
      </div>

      <div className={ clsx( 'layout__col', 'pad', styles.mTypeLLMPrompt ) }>
        <label
          className={ _inputRequiredCheck( INPUT_CONSTS.LLM_PROMPT ) }
          htmlFor={ INPUT_CONSTS.LLM_PROMPT }
          data-required-error={ _inputRequiredCheck( INPUT_CONSTS.LLM_PROMPT ) ? '' : undefined }
        >
          {INPUT_CONSTS.LLM_PROMPT}
        </label>
        <textarea
          defaultValue={ mTypeLLMPrompt }
          id={ INPUT_CONSTS.LLM_PROMPT }
          onChange={ ( e ) => setMTypeLLMPrompt( e.currentTarget.value ) }
          data-testid="grading-prompt-input"
        />
      </div>

      <div className={ clsx( 'layout__col', 'pad' ) }>
        <fieldset data-testid="prompt-parameters-section">
          <legend
            className={ clsx( 'auth_flow_label', _inputRequiredCheck( INPUT_CONSTS.LLM_PROMPT_PARAMETERS ) ) }
            data-required-error={ _inputRequiredCheck( INPUT_CONSTS.LLM_PROMPT_PARAMETERS ) ? '' : undefined }
          >
            {INPUT_CONSTS.LLM_PROMPT_PARAMETERS}
          </legend>
          {Object.entries( mTypeLLMPromptParams ).length > 0 && (
            <div className={ clsx( 'layout__col' ) }>
              <div className={ clsx( 'layout__row', styles.promptParamsHeader ) }>
                <p className={ clsx( 'auth_flow_label', styles.promptParamsLabel ) }>key</p>
                <p className={ clsx( 'auth_flow_label', styles.promptParamsLabel ) }>value</p>
                <div className={ styles.promptParamsSpacer }></div>
              </div>
              {Object.entries( mTypeLLMPromptParams ).map( ( [ key, value ], i ) => (
                <div key={ `prompt-param-row-${ i }` } className={ clsx( 'layout__row', styles.promptParamsRow ) }>
                  <input
                    className={ clsx( 'auth_flow_input', styles.promptParamsInput ) }
                    defaultValue={ key }
                    onChange={ ( e ) => onPromptParamChange( key, e.target.value, value ) }
                    type="text"
                    data-testid={ `prompt-param-key-${ i }` }
                  />
                  <input
                    className={ clsx( 'auth_flow_input', styles.promptParamsInput, isPromptParamValueMissing( key ) ? styles.required : '' ) }
                    defaultValue={ value }
                    onChange={ ( e ) => onPromptParamChange( key, key, e.target.value ) }
                    type="text"
                    data-testid={ `prompt-param-value-${ i }` }
                  />
                  <MdClose
                    onClick={ () => onPromptParamRemove( key ) }
                    className={ styles.promptParamsRemoveIcon }
                    data-testid={ `prompt-param-remove-${ i }` }
                  />
                </div>
              ) )}
            </div>
          )}
          <button
            onClick={ onPromptParamAdd }
            type="button"
            className="auth_flow_button_secondary"
            data-testid="add-prompt-param-button"
          >
            Add Parameter
          </button>
        </fieldset>
      </div>
    </>
  );

  const renderStepType = (): ReactElement => {
    let typeEls: ReactElement | undefined;

    switch ( metricType ) {
      case METRIC_TYPES.API_CLASSIFIER: {
        typeEls = renderStepTypeAPI();
        break;
      }
      case METRIC_TYPES.LLM_AS_A_JUDGE: {
        typeEls = renderStepTypeLLM();
        break;
      }
      default: {
        console.error( 'metric type not recognized' );
        typeEls = <></>;
        break;
      }
    }

    return (
      <>
        <Heading as="h2" size="md" data-testid="modal-title">{mode === 'clone' ? 'Clone' : 'Create'} Metric - {metricType} </Heading>
        {typeEls}
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label
            className={ clsx(
              'auth_flow_label',
              _inputRequiredCheck( INPUT_CONSTS.PRODUCES_METRICS )
            ) }
            htmlFor={ INPUT_CONSTS.PRODUCES_METRICS }
            data-testid="produces-metrics-label"
            data-required-error={ _inputRequiredCheck( INPUT_CONSTS.PRODUCES_METRICS ) ? '' : undefined }
          >
            {INPUT_CONSTS.PRODUCES_METRICS} (comma separated)
          </label>
          <input
            className={ clsx( 'auth_flow_input' ) }
            defaultValue={ producesMetrics.join( ',' ) }
            id={ INPUT_CONSTS.PRODUCES_METRICS }
            name={ INPUT_CONSTS.PRODUCES_METRICS }
            key={ INPUT_CONSTS.PRODUCES_METRICS }
            onChange={ ( e ) => setProducesMetrics( e.currentTarget.value.split( ',' ) ) }
            type="text"
            data-testid="produces-metrics-input"
          />
        </div>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <fieldset>
            <legend
              className={ clsx( _inputRequiredCheck( INPUT_CONSTS.EXAMPLE_TYPES ) ) }
              data-testid="example-types-label"
              data-required-error={ _inputRequiredCheck( INPUT_CONSTS.EXAMPLE_TYPES ) ? '' : undefined }
            >
              {INPUT_CONSTS.EXAMPLE_TYPES} (select all that apply)
            </legend>
            {Object.entries( exampleTypes ).map( ( [ key, value ] ) => {
              return (
                <div
                  className={ clsx( 'layout__row', 'flex_left' ) }
                  key={ `example_type-${ key }` }
                >
                  <input
                    id={ key }
                    name="example types"
                    onChange={ onSelectExampleType }
                    type="checkbox"
                    value={ key }
                    checked={ value }
                    data-testid={ `example-type-${ key }-checkbox` }
                  />
                  <label htmlFor={ key }>{key}</label>
                </div>
              );
            } )}
          </fieldset>
        </div>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button
            variant="secondary"
            size="sm"
            onClick={ onClose }
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={ _stepBack }
            data-testid="back-button"
          >
            Back
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={ _step }
            type="submit"
            data-testid="create-button"
            loading={ isSubmitting }
            disabled={ isSubmitting }
          >
            { isSubmitting ? ( mode === 'clone' ? 'Cloning...' : 'Creating...' ) : ( mode === 'clone' ? 'Clone' : 'Create' ) }
          </Button>
        </div>
      </>
    );
  };

  return <Modal>{_stepper()}</Modal>;
}
